import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Edit2, Save, RefreshCw, X, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppLayout from '@/components/layout/AppLayout';
import GoalSelector from '@/components/goals/GoalSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan',
  'China', 'India', 'Brazil', 'Mexico', 'South Korea', 'Singapore', 'Other'
];

const GOAL_LABELS = {
  'wealth_building': 'General Wealth Building',
  'retirement': 'Retirement',
  'home': 'Buying a Home',
  'education': "Children's Education",
  'debt_repayment': 'Debt Repayment',
  'specific_purchase': 'Specific Purchase (Car/Wedding)',
  'emergency_fund': 'Emergency Fund',
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [editedProfile, setEditedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userRes.data);

      const profileRes = await axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true });
      if (!profileRes.data) {
        navigate('/onboarding');
        return;
      }
      setProfile(profileRes.data);
      setEditedProfile(profileRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    navigate('/');
  };

  const updateField = (field, value) => {
    setEditedProfile(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleArrayField = (field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: (prev[field] || []).includes(value)
        ? prev[field].filter(v => v !== value)
        : [...(prev[field] || []), value]
    }));
  };

  const handleCancelEdit = () => {
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleSaveAndRegenerate = async () => {
    setIsSaving(true);
    try {
      toast.info('Saving your profile...');
      await axios.post(`${BACKEND_URL}/api/profile`, editedProfile, { withCredentials: true });
      
      toast.info('Regenerating your investment plan... This may take 30-60 seconds.');
      await axios.post(`${BACKEND_URL}/api/recommendations/generate`, {}, { withCredentials: true });
      
      toast.success('Plan regenerated successfully!');
      setProfile(editedProfile);
      setIsEditing(false);
      navigate('/plan');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate plan. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const displayProfile = isEditing ? editedProfile : profile;

  return (
    <AppLayout user={user} onLogout={handleLogout}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">My Profile</h1>
            <p className="text-muted-foreground">View and manage your financial information</p>
          </div>
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveAndRegenerate} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Save & Regenerate Plan
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="info" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              My Information
            </TabsTrigger>
            <TabsTrigger value="plan" className="flex items-center gap-2" onClick={() => navigate('/plan')}>
              <FileText className="w-4 h-4" />
              My Plan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-6">
            {/* Personal & Tax Information */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Personal & Tax Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Age</Label>
                  {isEditing ? (
                    <Input 
                      type="number" 
                      value={displayProfile?.age || ''} 
                      onChange={(e) => updateField('age', e.target.value)}
                    />
                  ) : (
                    <p className="font-medium">{displayProfile?.age || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">State of Residence</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.state_of_residence || ''} onValueChange={(val) => updateField('state_of_residence', val)}>
                      <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {US_STATES.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">{displayProfile?.state_of_residence || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Nationality</Label>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                      {COUNTRIES.map(country => (
                        <div key={country} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-nat-${country}`}
                            checked={(displayProfile?.nationality || []).includes(country)}
                            onCheckedChange={() => toggleArrayField('nationality', country)}
                          />
                          <Label htmlFor={`edit-nat-${country}`} className="font-normal text-sm">{country}</Label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">{(displayProfile?.nationality || []).join(', ') || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Tax Residence</Label>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                      {COUNTRIES.map(country => (
                        <div key={country} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-tax-${country}`}
                            checked={(displayProfile?.tax_residence || []).includes(country)}
                            onCheckedChange={() => toggleArrayField('tax_residence', country)}
                          />
                          <Label htmlFor={`edit-tax-${country}`} className="font-normal text-sm">{country}</Label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">{(displayProfile?.tax_residence || []).join(', ') || 'Not specified'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-muted-foreground">U.S. Tax Filing Status</Label>
                  {isEditing ? (
                    <RadioGroup value={displayProfile?.us_tax_filing_status || ''} onValueChange={(val) => updateField('us_tax_filing_status', val)} className="mt-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="citizen" id="edit-citizen" />
                        <Label htmlFor="edit-citizen">U.S. Citizen / Permanent Resident</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="nra" id="edit-nra" />
                        <Label htmlFor="edit-nra">Non-Resident Alien</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="expat" id="edit-expat" />
                        <Label htmlFor="edit-expat">U.S. Expat living abroad</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="foreign" id="edit-foreign" />
                        <Label htmlFor="edit-foreign">Foreign National</Label>
                      </div>
                    </RadioGroup>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.us_tax_filing_status?.replace('_', ' ') || 'Not specified'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Family & Income */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Family & Income</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Family Situation</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.family_situation || ''} onValueChange={(val) => updateField('family_situation', val)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married">Married/Partnered</SelectItem>
                        <SelectItem value="married_children">Married with children</SelectItem>
                        <SelectItem value="single_parent">Single parent</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.family_situation?.replace('_', ' ') || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Dependents</Label>
                  {isEditing ? (
                    <Input type="number" value={displayProfile?.dependents || ''} onChange={(e) => updateField('dependents', e.target.value)} />
                  ) : (
                    <p className="font-medium">{displayProfile?.dependents || '0'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Annual Income</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.annual_income || ''} onChange={(e) => updateField('annual_income', e.target.value)} placeholder="e.g., $150,000" />
                  ) : (
                    <p className="font-medium">{displayProfile?.annual_income || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Monthly Savings</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.monthly_savings || ''} onChange={(e) => updateField('monthly_savings', e.target.value)} placeholder="e.g., $3,000" />
                  ) : (
                    <p className="font-medium">{displayProfile?.monthly_savings || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Income Stability</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.income_stability || ''} onValueChange={(val) => updateField('income_stability', val)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="very_stable">Very stable</SelectItem>
                        <SelectItem value="stable">Stable</SelectItem>
                        <SelectItem value="variable">Variable</SelectItem>
                        <SelectItem value="unstable">Unstable</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.income_stability?.replace('_', ' ') || 'Not specified'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Assets */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Assets & Allocation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Net Assets</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.net_assets || ''} onChange={(e) => updateField('net_assets', e.target.value)} placeholder="e.g., $500,000" />
                  ) : (
                    <p className="font-medium">{displayProfile?.net_assets || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Liquid Assets</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.liquid_assets || ''} onChange={(e) => updateField('liquid_assets', e.target.value)} placeholder="e.g., $250,000" />
                  ) : (
                    <p className="font-medium">{displayProfile?.liquid_assets || 'Not specified'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label className="text-muted-foreground">Current Allocation</Label>
                  {displayProfile?.current_allocation ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                      {Object.entries(displayProfile.current_allocation).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-2 bg-muted rounded">
                          <span className="capitalize text-sm">{key.replace('_', ' ')}</span>
                          {isEditing ? (
                            <Select value={String(value)} onValueChange={(val) => updateNestedField('current_allocation', key, parseInt(val))}>
                              <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].map(pct => (
                                  <SelectItem key={pct} value={String(pct)}>{pct}%</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="font-medium">{value}%</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">Not specified</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Goals & Timeline */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Goals & Timeline</h2>
              <div className="space-y-6">
                <div>
                  <Label className="text-muted-foreground">Investment Goals (Ranked)</Label>
                  {isEditing ? (
                    <div className="mt-2">
                      <GoalSelector
                        value={displayProfile?.investment_goals || []}
                        onChange={(goals) => updateField('investment_goals', goals)}
                      />
                    </div>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {displayProfile?.investment_goals && displayProfile.investment_goals.length > 0 ? (
                        displayProfile.investment_goals
                          .sort((a, b) => a.rank - b.rank)
                          .map((goal, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-2 bg-muted rounded">
                              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                                {goal.rank}
                              </span>
                              <span className="font-medium">
                                {goal.is_custom ? goal.goal : (GOAL_LABELS[goal.goal] || goal.goal)}
                              </span>
                            </div>
                          ))
                      ) : displayProfile?.investment_goal ? (
                        <p className="font-medium capitalize">{displayProfile.investment_goal.replace('_', ' ')}</p>
                      ) : (
                        <p className="text-muted-foreground">Not specified</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Withdrawal Timeline</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.withdrawal_timeline || ''} onValueChange={(val) => updateField('withdrawal_timeline', val)}>
                      <SelectTrigger><SelectValue placeholder="Select timeline" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="<3y">Less than 3 years</SelectItem>
                        <SelectItem value="3-5y">3–5 years</SelectItem>
                        <SelectItem value="6-10y">6–10 years</SelectItem>
                        <SelectItem value=">10y">10+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">{displayProfile?.withdrawal_timeline || 'Not specified'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Risk & Knowledge */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Risk & Knowledge</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Investment Knowledge</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.investment_knowledge || ''} onValueChange={(val) => updateField('investment_knowledge', val)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.investment_knowledge || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Risk Preference</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.risk_chart_preference || ''} onValueChange={(val) => updateField('risk_chart_preference', val)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conservative">Conservative</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="aggressive">Aggressive</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.risk_chart_preference || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Emergency Fund (Months)</Label>
                  {isEditing ? (
                    <Input type="number" value={displayProfile?.emergency_fund_months || ''} onChange={(e) => updateField('emergency_fund_months', e.target.value)} />
                  ) : (
                    <p className="font-medium">{displayProfile?.emergency_fund_months || 'Not specified'} months</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Interest in Alternatives (Crypto)</Label>
                  {isEditing ? (
                    <RadioGroup value={displayProfile?.alternative_interest || ''} onValueChange={(val) => updateField('alternative_interest', val)} className="mt-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="edit-alt-yes" />
                        <Label htmlFor="edit-alt-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="edit-alt-no" />
                        <Label htmlFor="edit-alt-no">No</Label>
                      </div>
                    </RadioGroup>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.alternative_interest || 'Not specified'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Health & Debt */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Health & Debt</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Health Status</Label>
                  {isEditing ? (
                    <Select value={displayProfile?.health_status || ''} onValueChange={(val) => updateField('health_status', val)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="poor">Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.health_status || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Has Insurance</Label>
                  {isEditing ? (
                    <RadioGroup value={displayProfile?.has_insurance || ''} onValueChange={(val) => updateField('has_insurance', val)} className="mt-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="edit-ins-yes" />
                        <Label htmlFor="edit-ins-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="edit-ins-no" />
                        <Label htmlFor="edit-ins-no">No</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="some" id="edit-ins-some" />
                        <Label htmlFor="edit-ins-some">Some coverage</Label>
                      </div>
                    </RadioGroup>
                  ) : (
                    <p className="font-medium capitalize">{displayProfile?.has_insurance || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Total Debt</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.debt_total || ''} onChange={(e) => updateField('debt_total', e.target.value)} placeholder="e.g., $50,000" />
                  ) : (
                    <p className="font-medium">{displayProfile?.debt_total || 'None specified'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Monthly Debt Payment</Label>
                  {isEditing ? (
                    <Input value={displayProfile?.debt_monthly || ''} onChange={(e) => updateField('debt_monthly', e.target.value)} placeholder="e.g., $1,500" />
                  ) : (
                    <p className="font-medium">{displayProfile?.debt_monthly || 'None specified'}</p>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
